// Single-opportunity card — FE-SCRM-008 + FE-PINV-069 refresh +
// FE-SCRM-064c pencil-parity rework.
//
// Card layout mirrors the BeneCRM Tasks card pattern from
// ``~/Documents/benecrm_v2.pen`` (frame ``Renewal Board (redesigned)``,
// reusable component ``OpportunityCard (Renewal Board)``):
//
//   ┌─┬───────────────────────────────────┐
//   │▌│ Prospect name (full row, wraps 2L)│
//   │▌│ [BOR] Mid market                  │
//   │▌│ May 26                       ⓟ NP │
//   └─┴───────────────────────────────────┘
//
// The title owns its entire row — no longer competing with the type
// badge (which used to sit on the title row and squeeze the name into
// "Acme Manufactu…" on narrow columns). Type badge + lives band sit on
// a metadata row below. Footer is the Geist Mono renewal anchor on the
// left + producer avatar on the right.
//
// The hover-revealed action menu floats absolute in the top-right
// corner so opening it can't reflow the title row.
//
// Click still navigates to the detail page; drag (vertical) still
// changes stage and drag (horizontal) still changes renewal_month —
// the FE-SCRM-008 dataTransfer payload is preserved byte-for-byte so
// the existing drop handler in <Grid> keeps working.

import { AvatarCircle } from "@/design-system";

import {
  CadenceHealthEdge,
  OpportunityCardActionMenu,
} from "@/features/sales/components";

import { CADENCE_LABEL } from "./cadenceColors";
import type { RenewalBoardOpportunity } from "./types";


const TYPE_BADGE: Record<RenewalBoardOpportunity["opportunity_type"], string> = {
  new_business: "NEW",
  bor_takeover: "BOR",
  rfp_response: "RFP",
  expansion: "EXP",
};


const MONTH_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];


function renewalAnchorLabel(
  month: RenewalBoardOpportunity["renewal_month"],
  year: RenewalBoardOpportunity["renewal_year"],
): string {
  const monthLabel = MONTH_SHORT[month - 1] ?? String(month);
  const yearShort = String(year).slice(2).padStart(2, "0");
  return `${monthLabel} ${yearShort}`;
}


function livesBandLabel(
  band: RenewalBoardOpportunity["lives_band"],
): string | null {
  switch (band) {
    case "micro": return "Micro";
    case "small": return "Small";
    case "mid_market": return "Mid market";
    case "large": return "Large";
    case "enterprise": return "Enterprise";
    default: return null;
  }
}


interface Props {
  opp: RenewalBoardOpportunity;
  onClick?: (id: string) => void;
  onDragStart?: (e: React.DragEvent, opp: RenewalBoardOpportunity) => void;
}


export function OpportunityCard({ opp, onClick, onDragStart }: Props) {
  const lives = livesBandLabel(opp.lives_band);
  return (
    <div
      data-testid={`opp-card-${opp.opportunity_id}`}
      data-cadence-health={opp.cadence_health}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("text/plain", opp.opportunity_id);
        e.dataTransfer.setData(
          "application/x-renewal-board-opp",
          JSON.stringify({
            opportunity_id: opp.opportunity_id,
            stage: opp.stage,
            renewal_year: opp.renewal_year,
            renewal_month: opp.renewal_month,
          }),
        );
        e.dataTransfer.effectAllowed = "move";
        onDragStart?.(e, opp);
      }}
      onClick={(e) => {
        const target = e.target as HTMLElement;
        // Don't fire the card-level navigation when the producer
        // hits the kebab or a menu item inside the action menu.
        if (target.closest("[role='menu'], button")) return;
        onClick?.(opp.opportunity_id);
      }}
      title={`${CADENCE_LABEL[opp.cadence_health]} — ${opp.days_until_renewal} days to renewal`}
      className={[
        "group relative flex flex-col gap-2 overflow-hidden",
        "rounded-xl border border-border-subtle bg-raised pl-[14px] pr-3 py-3",
        "shadow-sm transition-fast ease-out cursor-pointer",
        "hover:border-border-strong",
        "focus-within:ring-2 focus-within:ring-teal",
      ].join(" ")}
    >
      <CadenceHealthEdge state={opp.cadence_health} variant="card" />

      {/* Action menu — absolute top-right corner so revealing it on
          hover doesn't reflow the title row below (the v1 layout had
          the menu sharing the title's flex row and pushing the name
          when hover-revealed). */}
      <div
        className={[
          "absolute right-2 top-2 z-10",
          "opacity-0 group-hover:opacity-100 focus-within:opacity-100",
          "transition-opacity",
        ].join(" ")}
      >
        <OpportunityCardActionMenu
          opportunity_id={opp.opportunity_id}
          prospect_name={opp.prospect_name}
          stage={opp.stage}
          opportunity_type={opp.opportunity_type}
          value_estimate={opp.value_estimate}
        />
      </div>

      {/* Title — full row. ``pr-6`` keeps text clear of the
          absolute-positioned action menu in the top-right corner. */}
      <div
        data-testid="opp-card-title"
        className={[
          "line-clamp-2 pr-6 text-sm font-medium leading-snug",
          "text-text-primary",
        ].join(" ")}
      >
        {opp.prospect_name}
      </div>

      {/* Metadata row — type badge + lives band + optional alert
          chip. Sits one line below the title so the badge no longer
          competes with the name for horizontal space. */}
      <div className="flex flex-wrap items-center gap-1.5 text-xs">
        <span
          data-testid="opp-card-type-badge"
          className={[
            "inline-flex items-center rounded bg-recessed px-1.5",
            "py-0.5 text-[10px] font-semibold tracking-wide",
            "text-text-secondary",
          ].join(" ")}
        >
          {TYPE_BADGE[opp.opportunity_type]}
        </span>
        {lives && (
          <span className="text-text-secondary">{lives}</span>
        )}
        {opp.form5500_alert_count > 0 && (
          <span
            data-testid="opp-card-alerts"
            className={[
              "inline-flex items-center gap-0.5 rounded-full",
              "bg-ember/10 px-1.5 py-0.5 text-[10px] text-ember-ink",
            ].join(" ")}
            title={`${opp.form5500_alert_count} active Form 5500 alert(s)`}
          >
            ⚑ {opp.form5500_alert_count}
          </span>
        )}
      </div>

      {/* Footer — renewal anchor (Geist Mono) on the left, producer
          avatar (when assigned) on the right. Mirrors the
          Commitment Tracker card's calendar/avatar footer rhythm. */}
      <footer className="flex items-center justify-between gap-2">
        <span
          data-testid="opp-card-renewal-anchor"
          className={[
            "font-mono text-[11px] tracking-[0.04em] tabular-nums",
            "text-text-tertiary",
          ].join(" ")}
        >
          {renewalAnchorLabel(opp.renewal_month, opp.renewal_year)}
        </span>
        {opp.producer_id && (
          <span data-testid="opp-card-producer-avatar">
            <AvatarCircle
              size="sm"
              initials="?"
              alt={`Producer ${opp.producer_id.slice(0, 4)}`}
            />
          </span>
        )}
      </footer>
    </div>
  );
}
